import { useEffect, useMemo, useRef, useState } from "react";

import { AgGridReact } from "ag-grid-react";
import Box from "@mui/material/Box";

import { withTranslation } from "react-i18next";
import { isEmpty } from "lodash";

import fpJobChangeService from "@plannel/services/fp/fp-jobchange-service";
import ResourcePopup from "@plannel/pages/factory-plan/common/popup/ResourcePopup";
import ResourceRoutePopup from "@plannel/pages/factory-plan/common/popup/ResourceRoutePopup";
import CodePickerRenderer from "@plannel/pages/factory-plan/common/component/CodePickerRenderer";
import Dialog from "@plannel/components/Dialog";
import Snackbar from "@plannel/components/Snackbar";
import DataState from "@plannel/components/aggrid/DataState";
import FpGridSetting, { useCommonCodeValues, codeComboCellEditorParams, findMissingRequired, focusInvalidCell } from "@plannel/pages/factory-plan/common/grid";
import { AddButton, DownloadButton, RemoveButton, SaveButton } from "@plannel/components/ActionIconButton";
import GridUtils from "@plannel/components/aggrid/GridUtils";
import debug from "@plannel/utils/debug-util";
import dateUtils from "@plannel/utils/date-util";
import ColorUtil from "@plannel/pages/utils/color-util";

const { snackBarMessageColor } = ColorUtil;

// old validRules required 3개(resourceCd/prevRouteCd/nextRouteCd) — Stock.js §REQUIRED_FIELDS 패턴.
const REQUIRED_FIELDS = [
  { field: "resourceCd", label: "FP_RESOURCE_CD" },
  { field: "prevRouteCd", label: "FP_PREV_ROUTE_CD" },
  { field: "nextRouteCd", label: "FP_NEXT_ROUTE_CD" }
];

/**
 * 공정간 작업 교체(Route Job Change) 탭 — old RouteJobChangeTab.jsx 대응. ItemJobChangeTab.js와
 * 동일 구성 + old의 전/후 공정 동일코드 방지 검증(showMessage 확인창 → Snackbar 경고로 대체).
 * 전/후 공정 선택은 old와 동일하게 `ResourceRoutePopup`(선택된 자원이 실제로 쓰는 공정만 조회 +
 * "ALL" 와일드카드)을 쓴다 — Bor 화면의 `RoutePopup`(전체 공정 무필터 조회)과는 다른 컴포넌트.
 *
 * props: t, viewName, planScope, searchFields
 */
const RouteJobChangeTab = ({ t, viewName, planScope, searchFields }) => {
  const gridRef = useRef();
  const [routeJobChange, setRouteJobChange] = useState([]);
  const [snackInfo, setSnackInfo] = useState({ open: false, severity: "", content: "" });
  const [dialogInfo, setDialogInfo] = useState({ open: false, title: "", content: "" });
  const [resourcePopup, setResourcePopup] = useState({ open: false, node: null });
  const [routePopup, setRoutePopup] = useState({ open: false, node: null, target: "" });

  const divideTpValuesRef = useCommonCodeValues("FP_DIVIDE_TP_CD");
  const timeUomValuesRef = useCommonCodeValues("FP_TIME_UOM");
  const latestReqRef = useRef(0);

  const defaultGridMemo = useMemo(() => ({
    ...FpGridSetting({ title: t("FP_ROUTE_JOB_CHANGE"), viewName }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [t, viewName]);

  // rowData는 절대 새 배열로 갈아치우지 않는다(§6-1 — getRowId 없는 상태에서 rowData 참조가 바뀌면
  // grid row model이 재생성되어 미저장 신규 행이 조회조건 변경 시 사라지는 버그가 있었다).
  const doesExternalFilterPass = (node) => {
    if (!node.data?.id) return true;   // 미저장 신규 행은 조회조건과 무관하게 항상 표시(편집 중 초안)
    const { resourceCdNm, prevRouteCd, nextRouteCd } = searchFields;
    const hit = (hay, needle) => (hay ?? "").toLowerCase().includes(needle.toLowerCase());
    const r = node.data;
    if (resourceCdNm && !(hit(r.resourceCd, resourceCdNm) || hit(r.resourceNm, resourceCdNm))) return false;
    if (prevRouteCd && !hit(r.prevRouteCd, prevRouteCd)) return false;
    if (nextRouteCd && !hit(r.nextRouteCd, nextRouteCd)) return false;
    return true;
  };

  const isExternalFilterPresent = () => Object.values(searchFields).some(v => !!v);

  // searchFields는 AG Grid가 직접 구독하는 값이 아니라서, 바뀔 때마다 명시적으로 재평가를 트리거.
  useEffect(() => {
    gridRef.current?.api?.onFilterChanged();
  }, [searchFields]);

  const openRoutePopup = (node, target) => {
    if (!node.data?.resourceCd) return;
    setRoutePopup({ open: true, node, target });
  };

  const columnDefs = [
    { headerName: "ID", field: "id", colId: "id", hide: true, type: ["nonEditableColumn"], cellDataType: false },
    { headerName: "FP_PLAN_SCOPE", field: "planScope", colId: "planScope", hide: true, type: ["nonEditableColumn"] },
    // old buttonVisibleCallback: isNewGridButtonVisible 대응 — 신규 행에서만 재선택 가능(§3-3).
    { headerName: "FP_RESOURCE_CD", field: "resourceCd", colId: "resourceCd", cellClass: "stringType", width: 180,
      editable: false, cellRenderer: CodePickerRenderer,
      cellRendererParams: { onOpenPopup: node => setResourcePopup({ open: true, node }), hideButtonWhenSaved: true } },
    { headerName: "FP_RESOURCE_NM", field: "resourceNm", colId: "resourceNm", type: ["nonEditableColumn"], width: 240 },
    // old buttonVisibleCallback: isGridResourceButtonVisible 대응 — resourceCd 선택 후 + 신규 행에서만(§3-4).
    { headerName: "FP_PREV_ROUTE_CD", field: "prevRouteCd", colId: "prevRouteCd", cellClass: "stringType", width: 180,
      editable: false, cellRenderer: CodePickerRenderer,
      cellRendererParams: { onOpenPopup: node => openRoutePopup(node, "prevRouteCd"), hideButtonWhenSaved: true, requiredField: "resourceCd" } },
    { headerName: "FP_PREV_ROUTE_NM", field: "prevRouteNm", colId: "prevRouteNm", type: ["nonEditableColumn"], width: 240 },
    { headerName: "FP_NEXT_JC_TM", field: "nextJcTime", colId: "nextJcTime", width: 110, type: ["numberColumn", "rightAligned"] },
    { headerName: "FP_PREV_JC_TM", field: "prevJcTime", colId: "prevJcTime", width: 110, type: ["numberColumn", "rightAligned"] },
    // old buttonVisibleCallback: isGridResourceButtonVisible 대응 — resourceCd 선택 후 + 신규 행에서만(§3-4).
    { headerName: "FP_NEXT_ROUTE_CD", field: "nextRouteCd", colId: "nextRouteCd", cellClass: "stringType", width: 180,
      editable: false, cellRenderer: CodePickerRenderer,
      cellRendererParams: { onOpenPopup: node => openRoutePopup(node, "nextRouteCd"), hideButtonWhenSaved: true, requiredField: "resourceCd" } },
    { headerName: "FP_NEXT_ROUTE_NM", field: "nextRouteNm", colId: "nextRouteNm", type: ["nonEditableColumn"], width: 240 },
    { headerName: "FP_TIME_UOM", field: "timeUom", colId: "timeUom", width: 120, type: ["centerAligned"],
      cellEditor: "agRichSelectCellEditor", cellEditorPopup: true,
      cellEditorParams: () => codeComboCellEditorParams("FP_TIME_UOM", timeUomValuesRef, t) },
    { headerName: "FP_JC_DIVIDE_TP_CD", field: "jcDivideTpCd", colId: "jcDivideTpCd", width: 110, type: ["centerAligned"],
      cellEditor: "agRichSelectCellEditor", cellEditorPopup: true,
      cellEditorParams: () => codeComboCellEditorParams("FP_DIVIDE_TP_CD", divideTpValuesRef, t) },
    {
      headerName: "FP_COL_AUDIT", groupId: "audit", marryChildren: true, openByDefault: false,
      children: [
        { headerName: "updatedTs", field: "modifyDttm", colId: "modifyDttm", type: ["nonEditableColumn", "centerAligned"], width: 150, valueFormatter: dateUtils.formatDateTime, filterType: "timestamp" },
        { headerName: "updatedBy", field: "modifyUser", colId: "modifyUser", type: ["nonEditableColumn", "centerAligned"], width: 110, filterType: "string", columnGroupShow: "open" },
        { headerName: "createdTs", field: "createDttm", colId: "createDttm", type: ["nonEditableColumn", "centerAligned"], width: 150, valueFormatter: dateUtils.formatDateTime, filterType: "timestamp", columnGroupShow: "open" },
        { headerName: "createdBy", field: "createUser", colId: "createUser", type: ["nonEditableColumn", "centerAligned"], width: 110, filterType: "string", columnGroupShow: "open" }
      ]
    }
  ];

  const retrieveRouteJobChange = () => {
    if (!planScope) return;
    const reqId = ++latestReqRef.current;
    gridRef.current?.api?.setGridOption("loading", true);

    fpJobChangeService.getRouteJobChanges({ planScope })
      .then(response => {
        if (reqId !== latestReqRef.current) return;
        const results = response.data;
        if (isEmpty(results)) {
          setRouteJobChange([]);
          setSnackInfo({ open: true, severity: snackBarMessageColor.dataEmpty, content: t("MSG_NoData") });
        } else {
          setRouteJobChange(results);
        }
      })
      .catch(e => debug(e))
      .then(() => {
        if (reqId !== latestReqRef.current) return;
        DataState.initialize(gridRef.current?.api);
      })
      .finally(() => {
        if (reqId === latestReqRef.current) gridRef.current?.api?.setGridOption("loading", false);
      });
  };

  const onGridReady = params => {
    GridUtils.setColumnDefs({ ...params, columnDefs, viewName, initState: true });
    retrieveRouteJobChange();
  };

  const insertRow = () => {
    const newRow = {
      id: null, planScope, resourceCd: "", resourceNm: "",
      prevRouteCd: "", prevRouteNm: "", nextRouteCd: "", nextRouteNm: "",
      nextJcTime: 0, prevJcTime: 0, timeUom: "MINUTE", jcDivideTpCd: "Y"
    };
    DataState.setDataState(GridUtils.addRow(gridRef.current.api, newRow));
  };

  const removeRouteJobChange = () => {
    gridRef.current.api.stopEditing();
    const selectedRows = gridRef.current.api.getSelectedRows();

    if (selectedRows.length > 0) {
      fpJobChangeService.removeRouteJobChanges(selectedRows)
        .then(() => setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_DeleteSuccess") }))
        .catch(() => setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_DeleteFailed") }))
        .then(() => retrieveRouteJobChange());
    } else {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
    }
  };

  const saveRouteJobChange = () => {
    gridRef.current.api.stopEditing();
    const allStateData = DataState.getAllStateData(gridRef.current.api);

    if (allStateData.length === 0) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.noSaveData, content: t("MSG_NoSaveData") });
      return;
    }

    // 실제로 비어 있는 필드만 안내한다 — common/grid.js의 findMissingRequired/focusInvalidCell(§9-2).
    const missingRequired = findMissingRequired(gridRef.current.api, REQUIRED_FIELDS);
    if (missingRequired.length > 0) {
      const labels = [...new Set(missingRequired.map(m => t(m.label)))].join("/");
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_PleaseEnterValues", { values: labels }) });
      focusInvalidCell(gridRef.current.api, missingRequired[0]);
      return;
    }

    fpJobChangeService.upsertRouteJobChanges(allStateData)
      .then(() => setSnackInfo({ open: true, severity: snackBarMessageColor.saveSuccess, content: t("MSG_SaveSuccess") }))
      .catch(e => { setSnackInfo({ open: true, severity: snackBarMessageColor.saveFail, content: t("MSG_SaveFailed") }); debug(e); })
      .then(() => retrieveRouteJobChange());
  };

  const onSelectResource = (row) => {
    const node = resourcePopup.node;
    if (node) {
      node.setDataValue("resourceCd", row.resourceCd);
      node.setDataValue("resourceNm", row.resourceNm);
      node.setDataValue("prevRouteCd", "");
      node.setDataValue("prevRouteNm", "");
      node.setDataValue("nextRouteCd", "");
      node.setDataValue("nextRouteNm", "");
    }
  };

  const onSelectRoute = (row) => {
    const { node, target } = routePopup;
    if (!node) return;

    // old: 전/후 공정에 같은 공정을 중복 선택하지 못하게 막는 검증.
    const other = target === "prevRouteCd" ? node.data?.nextRouteCd : node.data?.prevRouteCd;
    if (row.routeCd === other) {
      setSnackInfo({ open: true, severity: snackBarMessageColor.inputError, content: t("MSG_FP_NOT_SAME_ROUTE") });
      return;
    }

    if (target === "prevRouteCd") {
      node.setDataValue("prevRouteCd", row.routeCd);
      node.setDataValue("prevRouteNm", row.routeNm);
    } else if (target === "nextRouteCd") {
      node.setDataValue("nextRouteCd", row.routeCd);
      node.setDataValue("nextRouteNm", row.routeNm);
    }
  };

  const handleDialog = (action) => {
    if (action === "SAVE") saveRouteJobChange();
    else if (action === "DELETE") removeRouteJobChange();
  };

  useEffect(() => {
    if (!gridRef.current?.api) return;
    retrieveRouteJobChange();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planScope]);

  return (
    <>
      <Box sx={{ display: "flex", justifyContent: "flex-end", mb: 1 }}>
        <DownloadButton onClick={() => gridRef.current.api.exportDataAsExcel({ fileName: `${t("FP_ROUTE_JOB_CHANGE")}.xlsx`, sheetName: t("FP_ROUTE_JOB_CHANGE") })} />
        <AddButton onClick={insertRow} />
        <RemoveButton onClick={() => setDialogInfo({ open: true, title: "delete", content: t("MSG_DeleteConfirm") })} />
        <SaveButton onClick={() => setDialogInfo({ open: true, title: "save", content: t("MSG_SaveConfirm") })} />
      </Box>
      <Box id="fp-route-jc-grid" className="ag-theme-balham grid-area" sx={{ flex: 1, minHeight: 0, pb: 1 }}>
        <AgGridReact
          ref={gridRef}
          rowData={routeJobChange}
          {...defaultGridMemo}
          rowSelection={{
            ...defaultGridMemo.rowSelection,
            headerCheckbox: true,
            checkboxes: true
          }}
          isExternalFilterPresent={isExternalFilterPresent}
          doesExternalFilterPass={doesExternalFilterPass}
          onGridReady={onGridReady}
        />
      </Box>
      <ResourcePopup
        open={resourcePopup.open}
        onClose={() => setResourcePopup({ open: false, node: null })}
        planScope={planScope}
        onSelect={onSelectResource}
      />
      <ResourceRoutePopup
        open={routePopup.open}
        onClose={() => setRoutePopup({ open: false, node: null, target: "" })}
        planScope={planScope}
        resourceCd={routePopup.node?.data?.resourceCd}
        onSelect={onSelectRoute}
      />
      <Dialog
        open={dialogInfo.open}
        onClose={() => setDialogInfo({ open: false, title: "", content: "" })}
        title={dialogInfo.title}
        content={dialogInfo.content}
        onHandler={handleDialog}
      />
      <Snackbar
        open={snackInfo.open}
        onClose={() => setSnackInfo({ ...snackInfo, open: false })}
        severity={snackInfo.severity}
        content={snackInfo.content}
      />
    </>
  );
};

export default withTranslation()(RouteJobChangeTab);
